import type { VerdictV2 } from '../types';
import { SafetyScoreCard, BrandSafetyAgentCard, RiskBreakdownCard } from '../shared';
import { SafetyChecksCard } from './SafetyChecksCard';

interface CreatorProfile {
  content_safety_tier?: string;
  primary_topics?: string[];
  content_themes?: string[];
  audience_type?: string;
  audience_sentiment?: string;
  brand_collab_signals?: string;
  overall_summary?: string;
  brand_fit_summary?: string;
  fit_summary?: string;
}

/* ─── CreatorProfileSummary ──────────────────────────────────────────────── */

export function CreatorProfileSummary({
  profile,
  overallScore,
  overallRating,
  channelStats,
  verdictV2,
  onApprove,
}: {
  profile: CreatorProfile | null;
  overallScore?: number;
  overallRating?: string;
  onApprove?: () => void;
  verdictV2?: VerdictV2 | null;
  channelStats?: {
    scanned: number;
    last12Months: number;
    analyzed: number;
    isInstagram: boolean;
  };
}) {
  if (!profile) return null;
  const tier = String(profile.content_safety_tier ?? '').trim();
  const tierTone =
    tier.toLowerCase() === 'safe' ? { bg: '#ecfdf5', color: '#047857', ring: '#a7f3d0' }
    : tier.toLowerCase() === 'moderate' ? { bg: '#fffbeb', color: '#b45309', ring: '#fde68a' }
    : tier.toLowerCase() === 'high risk' || tier.toLowerCase() === 'risky' ? { bg: '#fef2f2', color: '#b91c1c', ring: '#fecaca' }
    : { bg: '#f5f5f5', color: '#404040', ring: '#e5e5e5' };
  const topics: string[] = Array.isArray(profile.primary_topics) ? profile.primary_topics : [];
  const themes: string[] = Array.isArray(profile.content_themes) ? profile.content_themes : [];
  const showScore = typeof overallScore === 'number';

  // Safety Checks — Figma 3-column layout (node 1741:55607).
  const hasSafetyChecks =
    topics.length > 0 || themes.length > 0 || !!profile.audience_type ||
    !!profile.audience_sentiment || !!profile.brand_collab_signals;
  const isSafe = tier.toLowerCase() === 'safe' || tier === '';
  const checkPill = isSafe
    ? { label: 'All clear', bg: 'rgba(5,150,105,0.12)', color: '#0B7A57' }
    : { label: tier, bg: tierTone.bg, color: tierTone.color };

  // Brand Safety Agent card — headline / status chips are driven by the
  // authoritative verdict tier (verdict_v2) when present, so they can never
  // disagree with the score gauge; content_safety_tier is only a fallback when
  // no verdict exists.
  const headlineTier = String(verdictV2?.tier ?? tier).trim();
  const headlineTierLc = headlineTier.toLowerCase();
  const headlineIsSafe = headlineTierLc === 'safe';
  const flaggedCount = verdictV2?.hard_blocks?.length ?? 0;
  const scans = channelStats?.scanned ?? channelStats?.analyzed ?? 0;
  const ratingSource = overallRating || (headlineIsSafe ? 'Safe' : headlineTier) || 'Reviewed';
  const ratingWord = ratingSource.charAt(0).toUpperCase() + ratingSource.slice(1);
  const chipColor = headlineIsSafe ? '#059669' : headlineTierLc === 'moderate' ? '#D97706' : '#DC2626';
  const agentHeadline = headlineIsSafe
    ? 'Clean and brand safe'
    : headlineTierLc === 'moderate'
      ? 'Mostly safe — review the flags'
      : headlineTier
        ? 'Brand safety risks found'
        : 'Brand safety reviewed';
  const agentChips: { label: string; color: string }[] = [
    { label: showScore ? `${ratingWord} · ${overallScore}/100` : ratingWord, color: chipColor },
    {
      label: flaggedCount === 0 ? 'Zero issues found' : `${flaggedCount} issue${flaggedCount !== 1 ? 's' : ''} found`,
      color: chipColor,
    },
    ...(headlineIsSafe ? [{ label: 'Brand safe · verified', color: '#059669' }] : []),
    ...(verdictV2?.needs_human_review ? [{ label: 'Human review required', color: '#D97706' }] : []),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginBottom: 24 }}>
      <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flexWrap: 'wrap' }}>
        {showScore && <SafetyScoreCard score={overallScore ?? 0} flaggedCount={flaggedCount} />}
        <BrandSafetyAgentCard
          scans={scans}
          headline={agentHeadline}
          description={profile.overall_summary || 'No summary returned by the AI.'}
          chips={agentChips}
          onApprove={onApprove}
        />
      </div>

      {hasSafetyChecks && (
        <SafetyChecksCard
          topics={topics}
          themes={themes}
          audienceType={profile.audience_type}
          audienceSentiment={profile.audience_sentiment}
          brandCollabSignals={profile.brand_collab_signals}
          aiSummary={profile.brand_fit_summary || profile.fit_summary}
          channelStats={channelStats}
          checkPill={checkPill}
        />
      )}

      {verdictV2?.category_scores && verdictV2.category_scores.length > 0 && (
        // Figma's Risk Breakdown has no coverage pill — that line lives only in
        // the Safety Checks header, so don't pass channelStats here.
        <RiskBreakdownCard scores={verdictV2.category_scores} />
      )}
    </div>
  );
}
